<template>
  <div class="space-y-2.5 p-3 rounded-lg border"
    :class="disabled ? 'bg-gray-50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-800' : 'bg-white dark:bg-[#0d1117] border-gray-200 dark:border-gray-700'">
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
      <div>
        <label class="block text-[10px] font-semibold uppercase tracking-wider mb-1" :class="disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'">
          Version <span class="normal-case font-normal text-gray-400 dark:text-gray-500">(optional)</span>
        </label>
        <div class="relative version-suggestions">
          <!-- Wrapper (not the input) carries the border, so it can fuse with
               the dropdown below — same combobox shape as the IT Component
               field (rounds top-only + drops its bottom border while open).
               Fixed height matches Alias/Released/EOL exactly. -->
          <div
            :class="[disabled ? 'bg-gray-50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-800 rounded-lg'
                     : (showSuggestions && suggestions.length ? 'rounded-t-lg border-b-transparent bg-gray-50 dark:bg-white/[0.04] border-gray-200 dark:border-gray-700' : 'rounded-lg bg-white dark:bg-[#0d1117] border-gray-200 dark:border-gray-700')]"
            class="w-full h-[34px] flex items-center gap-1.5 pl-3 pr-2.5 border">
            <!-- Same component icon shown once a version is present (typed,
                 selected, or pre-filled while editing) — ties this field back
                 to its parent IT component the way the Name field's own
                 matched-logo does. w-4 h-4 fits comfortably inside the fixed
                 34px row without stretching it. -->
            <ItComponentLogo v-if="componentName && version.trim()" :name="componentName" :logo-url="componentLogoUrl"
              size="w-4 h-4" initial-text-size="text-[7px]" class="flex-shrink-0" />
            <input ref="versionInput" v-model="version" type="text" placeholder="e.g. 16.4" autocomplete="off" :disabled="disabled"
              @keydown="handleVersionKeydown" @focus="showSuggestions = true" @input="showSuggestions = true"
              class="flex-1 min-w-0 text-sm bg-transparent border-0 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
              :class="disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'" />
            <svg v-if="!disabled" class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform"
              :class="showSuggestions && suggestions.length ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>

          <div v-if="!disabled && showSuggestions && suggestions.length"
            class="absolute z-30 top-full left-0 w-full bg-white dark:bg-[#1C2128] border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg shadow-lg py-1.5 max-h-40 overflow-y-auto">
            <button v-for="(s, index) in suggestions" :key="s.id" type="button"
              @click="selectVersion(s)" @mouseenter="activeSuggestionIndex = index"
              :class="index === activeSuggestionIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
              class="w-full flex items-center gap-2 text-left px-3 py-1.5 text-sm text-gray-800 dark:text-gray-100 transition-colors">
              <ItComponentLogo v-if="componentName" :name="componentName" :logo-url="componentLogoUrl" size="w-5 h-5" initial-text-size="text-[8px]" class="flex-shrink-0" />
              <span class="flex items-center gap-1.5">
                {{ s.version }}
                <!-- Already tracked on this org component — still selectable
                     (matches AddItComponentSlideOver's "Already added" cue for
                     the IT Component field), just flagged so re-picking it is
                     a deliberate choice. -->
                <span v-if="isAlreadyAdded(s)" class="inline-flex flex-shrink-0 items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                  Already added
                </span>
              </span>
            </button>
          </div>
        </div>
        <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">Select from the catalog, or type a new version.</p>
      </div>

      <div>
        <label class="block text-[10px] font-semibold uppercase tracking-wider mb-1" :class="disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'">
          Alias <span class="normal-case font-normal text-gray-400 dark:text-gray-500">(optional)</span>
        </label>
        <input v-model="alias" type="text" placeholder="e.g. Primary instance" :disabled="disabled"
          class="w-full px-3 py-1.5 text-sm border rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow disabled:cursor-not-allowed"
          :class="disabled ? 'bg-gray-50 dark:bg-gray-800/40 text-gray-400 dark:text-gray-500 border-gray-100 dark:border-gray-800' : 'bg-white dark:bg-[#0d1117] border-gray-200 dark:border-gray-700'" />
      </div>

      <div>
        <label class="block text-[10px] font-semibold uppercase tracking-wider mb-1" :class="disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'">
          Released <span class="normal-case font-normal text-gray-400 dark:text-gray-500">(optional)</span>
        </label>
        <DatePicker v-model="releasedDate" placeholder="Released" :disabled="disabled" input-class="px-3 py-1.5 text-sm rounded-lg" />
      </div>

      <div>
        <div class="flex items-center justify-between gap-2 mb-1">
          <label class="text-[10px] font-semibold uppercase tracking-wider" :class="disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'">
            <span title="End of life">EOL</span> <span class="normal-case font-normal text-gray-400 dark:text-gray-500">(optional)</span>
          </label>
          <label class="flex items-center gap-1 text-[10px] whitespace-nowrap" :class="disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400 cursor-pointer'">
            <input v-model="eolDateNotAnnounced" type="checkbox" :disabled="disabled" @change="() => { if (eolDateNotAnnounced) releasedDateGuard() }"
              class="w-3 h-3 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
              :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'" />
            Not announced
          </label>
        </div>
        <DatePicker v-model="eolDate" :disabled="disabled || eolDateNotAnnounced" placeholder="End of life" input-class="px-3 py-1.5 text-sm rounded-lg" />
      </div>
    </div>

    <div v-if="disabled && disabledMessage" class="flex items-center gap-1.5 pt-1">
      <svg class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/>
      </svg>
      <p class="text-xs text-gray-400 dark:text-gray-500 font-medium">{{ disabledMessage }}</p>
    </div>

    <template v-else>
      <p v-if="error" class="text-xs text-red-500 dark:text-red-400 font-medium">{{ error }}</p>

      <div class="flex gap-2 justify-end">
        <button type="button" @click="$emit('cancel')"
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer">
          Cancel
        </button>
        <button type="button" @click="submit" :disabled="saving"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors cursor-pointer">
          <svg v-if="saving" class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          {{ saving ? 'Saving...' : submitLabel }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useOrgGlobalComponentVersionsStore } from 'stores/orgGlobalComponentVersions.js'
import DatePicker from 'controllers/vue_components/shared/DatePicker.vue'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'

const props = defineProps({
  organizationId: { type: String, default: '' },
  globalComponentId: { type: String, default: '' },
  // The parent IT component's own identity — every version in this form
  // belongs to the same one, so its logo is shown in the Version field
  // (once a value is present) and against each suggestion row, tying this
  // field back to the component the way the IT Component field's own
  // matched-logo does. Callers already have this in scope (the slide-over's
  // matched catalog entry, or the factsheet application record).
  componentName: { type: String, default: '' },
  componentLogoUrl: { type: String, default: '' },
  initialVersion: { type: String, default: '' },
  initialAlias: { type: String, default: '' },
  initialReleasedDate: { type: String, default: '' },
  initialEolDate: { type: String, default: '' },
  initialEolNotAnnounced: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
  submitLabel: { type: String, default: 'Add version' },
  // Only a fresh Add opens the version dropdown and grabs focus straight
  // away — editing an already-resolved version shouldn't pop a dropdown or
  // steal focus the moment the row switches into its form.
  autoFocus: { type: Boolean, default: true },
  // No org component saved yet — renders every field inert (no catalog
  // fetch, no dropdown, no buttons) and shows disabledMessage instead.
  disabled: { type: Boolean, default: false },
  disabledMessage: { type: String, default: '' },
  // Lowercased version strings already tracked on this org component (the
  // caller excludes the row currently being edited, if any) — flags a
  // catalog suggestion as "Already added" the same way the IT Component
  // field flags a catalog entry the org already has.
  existingVersions: { type: Array, default: () => [] },
})

const emit = defineEmits(['cancel', 'submit'])

const catalogVersionsStore = useOrgGlobalComponentVersionsStore()

const versionInput = ref(null)
const version = ref(props.initialVersion)
const alias = ref(props.initialAlias)
const releasedDate = ref(toDateInput(props.initialReleasedDate))
const eolDate = ref(toDateInput(props.initialEolDate))
const eolDateNotAnnounced = ref(props.initialEolNotAnnounced)
const showSuggestions = ref(false)
const activeSuggestionIndex = ref(-1)

function toDateInput(iso) {
  return iso ? String(iso).slice(0, 10) : ''
}

function releasedDateGuard() {
  eolDate.value = ''
}

const catalogEntry = computed(() => catalogVersionsStore.entryFor(props.globalComponentId))
const suggestions = computed(() => {
  const query = version.value.trim().toLowerCase()
  const all = catalogEntry.value.records
  return (query ? all.filter((v) => v.version.toLowerCase().includes(query)) : all).slice(0, 8)
})

watch(suggestions, () => { activeSuggestionIndex.value = -1 })

function isAlreadyAdded(suggestion) {
  return props.existingVersions.includes(suggestion.version.toLowerCase())
}

function selectVersion(suggestion) {
  version.value = suggestion.version
  releasedDate.value = suggestion.released_date ? String(suggestion.released_date).slice(0, 10) : ''
  eolDate.value = suggestion.eol_date ? String(suggestion.eol_date).slice(0, 10) : ''
  if (suggestion.eol_date) eolDateNotAnnounced.value = false
  showSuggestions.value = false
  activeSuggestionIndex.value = -1
}

function handleVersionKeydown(e) {
  const open = showSuggestions.value && suggestions.value.length > 0
  if (open && e.key === 'ArrowDown') {
    e.preventDefault()
    activeSuggestionIndex.value = (activeSuggestionIndex.value + 1) % suggestions.value.length
  } else if (open && e.key === 'ArrowUp') {
    e.preventDefault()
    activeSuggestionIndex.value = activeSuggestionIndex.value <= 0 ? suggestions.value.length - 1 : activeSuggestionIndex.value - 1
  } else if (open && e.key === 'Escape') {
    showSuggestions.value = false
    activeSuggestionIndex.value = -1
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (open && activeSuggestionIndex.value >= 0) selectVersion(suggestions.value[activeSuggestionIndex.value])
    else submit()
  }
}

function submit() {
  emit('submit', {
    version: version.value.trim(),
    alias: alias.value.trim(),
    released_date: releasedDate.value || '',
    eol_date: eolDateNotAnnounced.value ? '' : (eolDate.value || ''),
    eol_date_not_announced: eolDateNotAnnounced.value,
  })
}

// Closes the dropdown on a click anywhere outside it. Registered on the
// capture phase (not bubble, and not scoped to a parent's own click handler
// like AddItComponentSlideOver's Name/Type fields do) so it still fires
// regardless of any ancestor calling stopPropagation() during bubble — this
// form is reused in several places (add row, in-place edit row, the
// Applications factsheet's VersionField) and can't assume a particular
// ancestor wiring in every one of them.
function handleDocumentClick(e) {
  if (!e.target.closest('.version-suggestions')) showSuggestions.value = false
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick, true)
  if (props.disabled) return

  catalogVersionsStore.fetchIfNeeded(props.organizationId, props.globalComponentId)
  if (props.autoFocus) {
    showSuggestions.value = true
    nextTick(() => versionInput.value?.focus())
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick, true)
})
</script>
